// HeroSection — **three cards** became **two** in W44 (W43 `ADR-0109` split the
// single lime block in three; W44 dropped the weakest third).
//
// W3.1 §5.6 built the original single card. W43 split it, because a 14.24:1 lime
// block three columns wide was holding a number, a countdown and a list at once —
// the most saturated thing the app can render, spent on the least interesting
// third of the content. Real-device screenshots of that card are the observation
// `ADR-0109` is based on.
//
// W44 then removed the 「即將到來」 preview. It was the one card on the screen with
// no job of its own: a saturated teal panel whose content was the first row of
// the `AnniversaryTimeline` sitting directly below it. The timeline is the real
// list; the hero now spends its only saturated block on a bare numeral, which is
// what lime is good at.
//
// Mobile stacks them; desktop puts the hero on its own row. The three
// `role="region"`s that existed when there were three cards are now two, and
// `HeroSection.test.tsx` asserts both by name — a screen-reader user reaches
// 「下一個紀念日」 and 「相處天數」, and the upcoming list is where it always was.
import { differenceInCalendarDays, parseISO } from "date-fns";

import { DaysTogetherCounter } from "./DaysTogetherCounter";
import { NextAnniversaryCountdown } from "./NextAnniversaryCountdown";
import { AccentCard, HeroCard } from "@/components/primitives/HeroCard";
import { ACCENT_ANNIVERSARIES, HERO_ANNIVERSARIES } from "@/lib/screenType";
import { readPair } from "@/lib/api/pair";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import type { AnniversaryNextResponse } from "@/lib/api/anniversary";
import { getNext } from "@/lib/api/anniversary";
import { queryKeys } from "@/lib/queries/keys";
import { cn } from "@/lib/utils/cn";

export interface HeroSectionProps {
  className?: string;
  /**
   * Total number of anniversaries in the pair.
   *
   * W44: **no longer read here.** It existed for the 「即將到來」 preview's
   * eyebrow (「共 N 個」), and that card is gone — the `AnniversaryTimeline`
   * below carries the count, where the full list lives. The prop stays on the
   * interface because `component-api.md §1` is additive-only and three call
   * sites pass it; removing it would be a breaking API change for a card that no
   * longer exists.
   */
  totalCount?: number | null;
}

export function HeroSection({ className }: HeroSectionProps) {
  const { pairId } = useAuth();
  const pairQuery = useQuery({
    queryKey: queryKeys.pairs.detail(pairId ?? ""),
    queryFn: () => readPair(pairId ?? ""),
    enabled: Boolean(pairId),
  });

  const today = new Date();

  const nextQuery = useQuery<AnniversaryNextResponse>({
    queryKey: queryKeys.anniversary.next(),
    queryFn: getNext,
    enabled: Boolean(pairId),
  });

  const anchorIso = pairQuery.data?.pair?.relationship_start ?? null;

  // Hero empty-state guard: no pair (anchor missing) AND no next.
  const anchorDays = anchorIso
    ? Math.max(0, differenceInCalendarDays(today, parseISO(anchorIso)))
    : 0;
  const hasHero = Boolean(anchorIso) || Boolean(nextQuery.data?.item);

  if (!hasHero) {
    return null;
  }

  // The anniversaries screen has **one** accent. The 「即將到來」 preview was the
  // second, and it was the weakest thing on the page: a saturated teal panel whose
  // only job was to repeat the first row of the `AnniversaryTimeline` rendered
  // directly below it. W44 removed it — the timeline is the real list, and the
  // hero now spends its only saturated block on a bare numeral, which is what
  // lime is actually good at.
  const { days: daysSlot } = ACCENT_ANNIVERSARIES;

  return (
    <div className={cn("flex flex-col gap-s-4", className)} data-testid="anniversaries-hero">
      {/* The hero: the countdown, because that is what the screen is for. */}
      <HeroCard
        slot={HERO_ANNIVERSARIES}
        label="下一個紀念日"
        describedBy="hero-meta"
        className="overflow-hidden"
        testId="anniversaries-hero-card"
      >
        <div id="hero-meta">
          <NextAnniversaryCountdown
            item={nextQuery.data?.item ?? null}
            isLoading={nextQuery.isLoading}
          />
        </div>
        {/* W52.0 D-5 — the rule is gone, the sentence stays.
            This was the **only** hairline inside this card, and it drew a line
            above a line of text that has no second column to be separated
            from. The card had already spent its structure on the countdown
            above; the rule read as a rendering leftover rather than a
            divider. */}
        {anchorDays > 3650 ? (
          <p className="mt-s-4 text-meta text-ink-1/70">
            交往超過 10 年了 · {anchorDays.toLocaleString("zh-Hant")}+ 天
          </p>
        ) : null}
      </HeroCard>

      {daysSlot ? (
        <AccentCard
          slot={daysSlot}
          label="相處天數"
          testId="anniversaries-days-card"
          className="overflow-hidden"
        >
          <DaysTogetherCounter anchorIso={anchorIso} now={today} />
        </AccentCard>
      ) : null}
    </div>
  );
}